import React from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@material-ui/core';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import OtpInput from 'react-otp-input';
import useMpinStyles from './useMpinStyles';
import { formatMmSs } from './mpin.helper';

/**
 * Labeled, masked MPIN cell row. One component for every field across all
 * three variants. Raw values flow through props only — never logged.
 */
export const MpinCellInput = ({
    label,
    value,
    onChange,
    length,
    hasError,
    errorMessage,
    disabled,
    autoFocus,
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    length: number;
    hasError: boolean;
    errorMessage?: string;
    disabled: boolean;
    autoFocus: boolean;
}) => {
    const styles = useMpinStyles();
    return (
        <Box className={styles.fieldBlock}>
            <Typography component="label" className={styles.fieldLabel}>
                {label}
            </Typography>
            <OtpInput
                value={value}
                onChange={onChange}
                numInputs={length}
                isInputNum
                isInputSecure
                shouldAutoFocus={autoFocus}
                isDisabled={disabled}
                hasErrored={hasError}
                inputStyle={styles.otpInputStyle}
                errorStyle={styles.otpError}
                separator={<span style={{ width: 8 }} />}
            />
            {hasError && !!errorMessage && (
                <Typography className={styles.fieldError}>
                    {errorMessage}
                </Typography>
            )}
        </Box>
    );
};

/**
 * Red lockout panel with the live countdown — replaces the input fields.
 * The copy is fixed on the web: the server's lockout message says
 * "contact your vendor", which only fits the rider app — web users go
 * through LoadShare Support instead.
 */
export const LockoutPanel = ({ secondsLeft }: { secondsLeft: number }) => {
    const styles = useMpinStyles();
    return (
        <Box className={styles.lockoutPanel}>
            <Typography className={styles.lockoutTitle}>
                Too many incorrect attempts
            </Typography>
            <Typography className={styles.lockoutTimer}>
                {formatMmSs(secondsLeft)}
            </Typography>
            <Typography className={styles.lockoutMessage}>
                Your PIN is locked temporarily. For help, contact LoadShare
                Support.
            </Typography>
            <Typography className={styles.lockoutHint}>
                You'll be signed out when the timer ends — log in with OTP to
                continue.
            </Typography>
        </Box>
    );
};

/** Server-driven banner copy, e.g. "Your PIN was reset. Set a new one…". */
export const MpinBanner = ({ message }: { message: string }) => {
    const styles = useMpinStyles();
    return <Box className={styles.banner}>{message}</Box>;
};

/**
 * Forgot MPIN on the web deliberately makes NO API call — resets are
 * support-mediated. This dialog is the entire flow.
 */
export const ForgotMpinDialog = ({
    open,
    onClose,
}: {
    open: boolean;
    onClose: () => void;
}) => (
    <Dialog open={open} onClose={onClose} maxWidth="xs">
        <DialogTitle disableTypography>
            <Typography
                style={{ fontFamily: 'Nunito', fontWeight: 700, fontSize: 18 }}
            >
                Forgot your PIN?
            </Typography>
        </DialogTitle>
        <DialogContent>
            <Typography
                style={{ fontFamily: 'Nunito', fontSize: 14, color: '#3d4757' }}
            >
                For security, a PIN can't be reset from the web. Please contact
                your supervisor or LoadShare support and ask for a PIN reset.
                After it's reset, log in again — you'll be asked to set a new
                PIN.
            </Typography>
        </DialogContent>
        <DialogActions>
            <Button
                onClick={onClose}
                color="primary"
                variant="contained"
                disableElevation
                style={{ textTransform: 'none', fontFamily: 'Nunito' }}
            >
                Got it
            </Button>
        </DialogActions>
    </Dialog>
);

/**
 * Warning shown when the user tries to BACK out of the PIN gate (browser
 * back, keyboard back, mobile hardware back — every path arrives as a
 * history POP). Confirming logs out: local storage is cleared and the
 * user lands on the login screen; cancelling stays on the gate.
 */
export const ExitMpinDialog = ({
    open,
    onConfirm,
    onCancel,
}: {
    open: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}) => (
    <Dialog open={open} onClose={onCancel} maxWidth="xs">
        <DialogTitle disableTypography>
            <Typography
                style={{ fontFamily: 'Nunito', fontWeight: 700, fontSize: 18 }}
            >
                Leave this page?
            </Typography>
        </DialogTitle>
        <DialogContent>
            <Typography
                style={{ fontFamily: 'Nunito', fontSize: 14, color: '#3d4757' }}
            >
                Going back will log you out. You'll need to log in with OTP
                again to complete the PIN step.
            </Typography>
        </DialogContent>
        <DialogActions>
            <Button
                onClick={onCancel}
                disableRipple
                style={{
                    textTransform: 'none',
                    fontFamily: 'Nunito',
                    color: '#5c6470',
                }}
            >
                Stay
            </Button>
            <Button
                onClick={onConfirm}
                variant="contained"
                disableElevation
                style={{
                    textTransform: 'none',
                    fontFamily: 'Nunito',
                    backgroundColor: '#d32f2f',
                    color: '#fff',
                }}
            >
                Log out
            </Button>
        </DialogActions>
    </Dialog>
);

/** Brief post-success confirmation (setup/change only). */
export const MpinSuccessTick = ({ title }: { title: string }) => {
    const styles = useMpinStyles();
    return (
        <Box className={styles.successBlock}>
            <Box className={styles.successCircle}>
                <CheckRoundedIcon style={{ fontSize: 40 }} />
            </Box>
            <Typography className={styles.successTitle}>{title}</Typography>
        </Box>
    );
};
